const ONE_DAY_IN_MILLISECONDS = 86400000;

type UnderlyingValue = { timestamp: number; value: { ttl?: number; value: boolean } };
type UnderlyingStorage<Keys extends string> = Record<Keys, UnderlyingValue>;

export type CacheController<Keys extends string> = {
	delete: () => void;
	get: () => Partial<UnderlyingStorage<Keys>>;
	set: (value: Partial<UnderlyingStorage<Keys>>) => void;
};

type EligibilityCache<Keys extends string> = {
	delete: (key: Keys) => Promise<void>;
	deleteAll: () => Promise<void>;
	get: (key: Keys) => Promise<boolean | void>;
	set: (key: Keys, value: boolean, ttlDays?: number) => Promise<void>;
};

export function createMutex() {
	let queue: Array<() => void> = [];
	let locked = false;

	const acquireLock = (): Promise<() => void> => {
		return new Promise((resolve) => {
			const releaseLock = () => {
				locked = false;
				processQueue();
			};

			const grantLock = () => {
				locked = true;
				resolve(releaseLock);
			};

			if (locked) {
				queue.push(() => {
					grantLock();
				});
			} else {
				grantLock();
			}
		});
	};

	const processQueue = () => {
		if (queue.length > 0) {
			const resolveNextItem = queue.shift();
			if (resolveNextItem) {
				resolveNextItem();
			}
		}
	};
	return { acquireLock };
}

export function createEligibilityCache<Keys extends string>(
	control: CacheController<Keys>,
): EligibilityCache<Keys> {
	const mutex = createMutex();

	function getControlCache(): UnderlyingStorage<Keys> {
		const possibleObject = control.get();
		if (possibleObject === undefined || possibleObject === null || possibleObject === '') {
			return {} as UnderlyingStorage<Keys>;
		}
		return possibleObject as UnderlyingStorage<Keys>;
	}

	return {
		get: async (key: Keys): Promise<boolean | undefined> => {
			const releaseLock = await mutex.acquireLock();
			const storedValues = getControlCache();
			const storedCheck = storedValues[key];
			if (storedCheck !== null && storedCheck !== undefined) {
				const {
					value: { value, ttl },
				} = storedCheck;
				const pastTtl = ttl ? ttl < Date.now() : false;
				if (pastTtl) {
					releaseLock();
					return undefined;
				}
				releaseLock();
				return value;
			}
			releaseLock();
			return undefined;
		},

		set: async (key: Keys, value: boolean, daysToLive?: number) => {
			const releaseLock = await mutex.acquireLock();
			const storedValues = getControlCache();
			const keyValue: UnderlyingValue = {
				value: {
					value,
					...(daysToLive
						? { ttl: new Date().getTime() + ONE_DAY_IN_MILLISECONDS * daysToLive }
						: {}),
				},
				timestamp: Date.now(),
			};
			storedValues[key] = keyValue;
			control.set(storedValues);
			releaseLock();
		},

		delete: async (key: Keys) => {
			const releaseLock = await mutex.acquireLock();
			const object = control.get();
			delete object[key];
			control.set(object);
			releaseLock();
		},

		deleteAll: async () => {
			const releaseLock = await mutex.acquireLock();
			control.delete();
			releaseLock();
		},
	};
}

export function createWithEligibilityCache<Keys extends string>(cache: EligibilityCache<Keys>) {
	function withEligibilityCache<A extends unknown[]>(
		key: Keys,
		eligibilityCheck: (...args: A) => Promise<boolean>,
		daysToLive?: number,
	) {
		return async (...args: A): Promise<boolean> => {
			try {
				const cachedValue = await cache.get(key);
				if (cachedValue !== undefined && cachedValue !== null) {
					return cachedValue;
				}
			} catch (e) {
				// cache get fail, do nothing
			}

			const result = await eligibilityCheck(...args);
			try {
				await cache.set(key, result, daysToLive);
			} catch (e) {
				// cache set fail, do nothing
			}
			return result;
		};
	}

	return withEligibilityCache;
}
